import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectMetrics } from "../../store/selectors/metricSelectors";
import { fetchMetricData } from "../../store/thunks/metricThunks";
import { mapDefectsMetrics } from "../../utils/DashboardHelper";
import { RecurringDefectsGraph } from "./RecurringDefectsGraph";
import { PlotMouseEvent } from "plotly.js";
import { defaultDefectsFiltersConfig } from "../DefectsTable/Filters/FilterTemplates";
import { DefectTableFields } from "../DefectsTable/DefectTableFields";
import { FilterMatchMode } from "primereact/api";
import { resetTableFilters } from "../../store/thunks/filtersThunks";
import LOCAL_TABLE_CONFIG_KEY from "../../types/TableLocalConfigKeys";
import { setActiveTab } from "../../store/slices/defectSlice";
import { DefectTabs } from "../../types/DefectTabs";
import { ProgressSpinner } from "primereact/progressspinner";

export const RecurringDefectsTab = () => {
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const { data: metricsData, loading } = useAppSelector(selectMetrics);

    useEffect(() => {
        if (!metricsData && !loading) {
            dispatch(fetchMetricData());
        }
    }, [metricsData, loading, dispatch]);

    const onBarClick = useCallback(
        (event: PlotMouseEvent) => {
            if (event.points.length && metricsData?.recurring_defects?.recurring_defects) {
                const { y, data, pointIndex } = event.points[0];
                const typeCode = data.customdata[pointIndex];
                if (y && typeof y === "string" && typeof typeCode === "string") {
                    // Find the metric with this type code to get IMO numbers
                    const metric = metricsData.recurring_defects.recurring_defects.find(m => m.type_code === typeCode);
                    const imoNumbers = metric?.imo_numbers?.map(String) || [];

                    const tableFilters = {
                        ...defaultDefectsFiltersConfig,
                        [DefectTableFields.STATUS]: {
                            value: null,
                            matchMode: FilterMatchMode.IN
                        },
                        [DefectTableFields.TYPE_CODE]: {
                            value: [typeCode],
                            matchMode: FilterMatchMode.IN
                        },
                        ...(imoNumbers.length > 0 && {
                            [DefectTableFields.IMO]: {
                                value: imoNumbers,
                                matchMode: FilterMatchMode.IN
                            }
                        })
                    };

                    dispatch(
                        resetTableFilters(
                            LOCAL_TABLE_CONFIG_KEY.DEFECTS,
                            tableFilters
                        )
                    );

                    dispatch(setActiveTab(DefectTabs.defects));
                }
            }
        },
        [metricsData, dispatch]
    );

    if (loading && !metricsData?.recurring_defects?.recurring_defects?.length) {
        return (
            <div className="flex justify-content-center align-items-center h-20rem">
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="card defect-graph-container">
            <div className="flex justify-content-between align-items-center mb-3">
                <h2 className="m-0">{t("defectsGraph.recurringDefects")}</h2>
            </div>
            <RecurringDefectsGraph
                className="defect-insight-graph"
                metrics={metricsData?.recurring_defects?.recurring_defects}
                filterValues={[]}
                loading={loading}
                onClick={onBarClick}
                maxItems={10}
            />
        </div>
    );
};
